import {
  AsyncThunk,
  CreateSliceOptions,
  PayloadAction,
  SliceCaseReducers,
  ValidateSliceCaseReducers,
} from '@reduxjs/toolkit';
import { perPageLimit } from '@app/definition/constants';
import { AccountData } from '@app/session';
import { SetUserDataThunk } from '@app/session/types';
import { ColumnValues, OrderValues } from '../DataTable/types';
import { DateFilterType } from '../../designSystem/dateFilter';

export interface ListFiltersI {
  page: number;
  limit: number;
  column: ColumnValues;
  order: OrderValues;
  startDate: null | string;
  endDate: null | string;
  searchText?: string;
}

export type ItemsType = Record<string, unknown>[];

export interface ListStateI<FiltersI extends ListFiltersI> {
  loading: boolean;
  items: ItemsType;
  totalCount: number;
  filters: FiltersI;
}

export type SortDataI = {
  column: ColumnValues;
  order: OrderValues;
};

export const listInitialState: ListStateI<ListFiltersI> = {
  loading: true,
  items: [],
  totalCount: 0,
  filters: {
    page: 1,
    limit: perPageLimit,
    column: '',
    order: 'asc',
    startDate: null,
    endDate: null,
  },
};

export const getListReducers = <
  FiltersI extends ListFiltersI,
  StateI extends ListStateI<FiltersI>
>(): ValidateSliceCaseReducers<StateI, SliceCaseReducers<StateI>> => ({
  setLoader: (state, action: PayloadAction<boolean>): void => {
    state.loading = action.payload;
  },
  setFilters: (state, action: PayloadAction<Partial<ListFiltersI>>): void => {
    const { filters } = state;
    state.filters = {
      ...filters,
      page: 1,
      ...action.payload,
    };
  },
  setPageNumber: (state, action: PayloadAction<number>): void => {
    const { filters } = state;
    state.filters = { ...filters, page: action.payload };
  },
  setPageLimit: (state, action: PayloadAction<number | 'all'>): void => {
    const { filters, totalCount } = state;
    const perPage = action.payload === 'all' ? totalCount : action.payload;
    state.filters = { ...filters, page: 1, limit: perPage };
  },
  setSorting: (state, action: PayloadAction<SortDataI>): void => {
    const { filters } = state;
    state.filters = { ...filters, ...action.payload, page: 1 };
  },
  setSearchText: (state, action: PayloadAction<string>): void => {
    const { filters } = state;
    state.filters = { ...filters, searchText: action.payload, page: 1 };
  },
  setDate: (state, action: PayloadAction<DateFilterType>): void => {
    const { filters } = state;
    state.filters = {
      ...filters,
      page: 1,
      ...action.payload,
    };
  },
});

export interface FetchActionResultI<StateI extends ListStateI<ListFiltersI>> {
  data: StateI['items'];
  total: number;
}

export const getListExtraReducers = <
  StateI extends ListStateI<ListFiltersI>,
  ResultI extends FetchActionResultI<StateI> = FetchActionResultI<StateI>
>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchAction: AsyncThunk<ResultI, any, any>,
  setUserDataThunk: SetUserDataThunk
): CreateSliceOptions['extraReducers'] => ({
  [fetchAction.fulfilled.type]: (
    state,
    action: PayloadAction<{ data: StateI['items']; total: number }>
  ): void => {
    state.loading = false;
    state.items = action.payload.data;
    state.totalCount = action.payload.total;
  },
  [fetchAction.rejected.type]: (state): void => {
    state.loading = false;
    state.items = [];
  },
  [fetchAction.pending.type]: (state): void => {
    state.loading = true;
  },
  [setUserDataThunk.fulfilled.type]: (
    state,
    action: PayloadAction<AccountData>
  ): void => {
    if (!action.payload) {
      Object.assign(state, {
        loading: listInitialState.loading,
        items: listInitialState.items,
        totalCount: listInitialState.totalCount,
      });
      Object.assign(state.filters, listInitialState.filters);
    }
  },
});
